import { useEffect, useRef, useState, useCallback } from 'react';
import { getWebSocketToken } from '@/api/auth';
import { fetchChatHistory } from '@/api/chat';
import { useCurrentUserModule } from './useUserModule';
import { useNoGuard } from './useNoGuard';
import { mockChatHistory, getMockAIResponse } from '@/mocks/mockChatMessages';
import { EngagementTracker } from '@/components/chat/EngagementTracker';
import { authErrorHandler } from '@/utils/authErrorHandler';
import {
  WebSocketMessage,
  ConnectionEstablishedMessage,
  CompletionSignalMessage,
  ThinkingMessage,
  SummaryDraftMessage,
} from '@/types/chatCompletion';

export interface ChatMessage {
  id?: number;
  text: string;
  fromUser: boolean;
  timestamp: string;
  userModuleId?: number;
}

interface UseEnhancedWebSocketChatProps {
  onMessageReceived?: (message: ChatMessage) => void;
  onCompletionSignal?: (data: CompletionSignalMessage['metadata']) => void;
  onSummaryDraft?: (data: SummaryDraftMessage['metadata']) => void;
}

export function useEnhancedWebSocketChat({
  onMessageReceived,
  onCompletionSignal,
  onSummaryDraft,
}: UseEnhancedWebSocketChatProps = {}) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentResponse, setCurrentResponse] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [completionStage, setCompletionStage] = useState<string>('chatting');
  const [isThinking, setIsThinking] = useState(false);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentResponseRef = useRef<string>('');
  const engagementTrackerRef = useRef<EngagementTracker | null>(null);
  const completionStageRef = useRef<string>('chatting');
  const messagesRef = useRef<ChatMessage[]>([]);
  const hasSummaryRef = useRef<boolean>(false);
  
  const { data: currentModule } = useCurrentUserModule();
  const isNoGuard = useNoGuard();
  
  // Keep messagesRef in sync with messages state
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Initialize engagement tracker
  useEffect(() => {
    if (!isNoGuard && !engagementTrackerRef.current) {
      engagementTrackerRef.current = new EngagementTracker();
    }
    
    return () => {
      if (engagementTrackerRef.current) {
        engagementTrackerRef.current.cleanup();
        engagementTrackerRef.current = null;
      }
    };
  }, [isNoGuard]);

  // Load chat history on mount
  useEffect(() => {
    const loadHistory = async () => {
      if (isNoGuard) {
        setMessages(mockChatHistory);
        setIsConnected(true);
        setIsLoading(false);
        return;
      }
      
      try {
        const history = await fetchChatHistory();
        const transformedHistory = history.map((msg: ChatMessage) => ({
          id: msg.id,
          text: msg.text,
          fromUser: msg.fromUser,
          timestamp: msg.timestamp,
          userModuleId: msg.userModuleId
        }));
        
        if (transformedHistory.length === 0) {
          const welcomeMessage: ChatMessage = {
            text: "Hello! I'm your AI coach, and I'm here to help you navigate change and develop your leadership skills. Based on your PULSE survey results, I can see you're ready to make positive changes. What would you like to explore today?",
            fromUser: false,
            timestamp: new Date().toISOString(),
            userModuleId: currentModule?.id
          };
          setMessages([welcomeMessage]);
        } else {
          const reversedHistory = transformedHistory.reverse();
          setMessages(reversedHistory);
        }
      } catch (error) {
        console.error('Failed to load chat history:', error);
        const welcomeMessage: ChatMessage = {
          text: "Hello! I'm your AI coach, and I'm here to help you navigate change and develop your leadership skills. What challenges or opportunities would you like to discuss today?",
          fromUser: false,
          timestamp: new Date().toISOString(),
          userModuleId: currentModule?.id
        };
        setMessages([welcomeMessage]);
      } finally {
        setIsLoading(false);
      }
    };

    loadHistory();
  }, [currentModule?.id, isNoGuard]);

  // Use ref for stable message handler
  const messageHandlerRef = useRef<(event: MessageEvent) => void>();
  
  // Update the message handler
  useEffect(() => {
    messageHandlerRef.current = (event: MessageEvent) => {
      try {
        const data: WebSocketMessage = JSON.parse(event.data);
        console.log('WebSocket message received:', data.type, 'completionStage:', completionStage);
        
        switch (data.type) {
          case 'connection_established':
            const connMsg = data as ConnectionEstablishedMessage;
            setSessionId(connMsg.metadata.sessionId);
            setCompletionStage(connMsg.metadata.completionStage);
            completionStageRef.current = connMsg.metadata.completionStage;
            console.log('Connection established:', connMsg.metadata);
            break;
            
          case 'partial':
            // Log what we're receiving during summarizing
            if (completionStageRef.current === 'summarizing') {
              console.log('Received partial during summarizing:', data.text);
            }
            // Don't show partial messages when generating summary
            if (completionStageRef.current !== 'summarizing') {
              setCurrentResponse(prev => {
                const newResponse = prev + data.text;
                currentResponseRef.current = newResponse;
                return newResponse;
              });
            }
            break;
            
          case 'done':
            // Log what we're receiving during summarizing
            if (completionStageRef.current === 'summarizing') {
              console.log('Received done during summarizing:', data.text || currentResponseRef.current);
            }
            // Don't add messages to chat when generating summary
            if (completionStageRef.current !== 'summarizing') {
              const finalText = currentResponseRef.current || data.text;
              if (finalText) {
                const completeMessage: ChatMessage = {
                  text: finalText,
                  fromUser: false,
                  timestamp: new Date().toISOString(),
                  userModuleId: currentModule?.id
                };
                
                setMessages(prev => [...prev, completeMessage]);
                
                if (onMessageReceived) {
                  onMessageReceived(completeMessage);
                }
              }
            }
            setCurrentResponse('');
            currentResponseRef.current = '';
            break;
            
          case 'completion_signal':
            const completionMsg = data as CompletionSignalMessage;
            if (onCompletionSignal) {
              onCompletionSignal(completionMsg.metadata);
            }
            break;
            
          case 'analysis_request':
            // Legacy code - Backend no longer uses this flow
            // The backend now calculates all metrics internally when we send 'generate_summary'
            // Keeping this handler for backwards compatibility but it shouldn't be triggered
            console.warn('Received unexpected analysis_request - this flow is deprecated');
            break;
            
          case 'thinking':
            const thinkingMsg = data as ThinkingMessage;
            setIsThinking(thinkingMsg.metadata.show);
            break;
            
          case 'summary_draft':
            console.log('Received summary_draft message:', data);
            const summaryMsg = data as SummaryDraftMessage;
            setCompletionStage('reviewing');
            completionStageRef.current = 'reviewing';
            setIsGeneratingSummary(false);
            hasSummaryRef.current = true;
            if (onSummaryDraft) {
              console.log('Calling onSummaryDraft with:', summaryMsg.metadata);
              onSummaryDraft(summaryMsg.metadata);
            } else {
              console.error('No onSummaryDraft handler provided!');
            }
            break;
            
          case 'error':
            // Reset generating state on error
            setIsGeneratingSummary(false);
            if (completionStageRef.current === 'summarizing') {
              setCompletionStage('chatting');
              completionStageRef.current = 'chatting';
            }
            console.error('Chat error:', data.text || 'An error occurred');
            break;
        }
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };
  }, [currentModule?.id, onMessageReceived, onCompletionSignal, onSummaryDraft]);

  // Stable message handler
  const handleWebSocketMessage = useCallback((event: MessageEvent) => {
    messageHandlerRef.current?.(event);
  }, []);

  // Use ref for stable connect function
  const connectRef = useRef<() => Promise<void>>();
  
  // Update connect function
  useEffect(() => {
    connectRef.current = async () => {
      // Don't connect if already connecting/connected
      if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
        console.log('WebSocket already connecting/connected, skipping');
        return;
      }

      try {
        const token = await getWebSocketToken();
        const wsBaseUrl = import.meta.env.VITE_API_BASE_URL?.replace('http', 'ws') || 'ws://localhost:8000';
        const userModuleId = currentModule?.id;
        const wsUrl = `${wsBaseUrl}/api/chat/ws/chat?token=${token}${userModuleId ? `&user_module_id=${userModuleId}` : ''}`;
        
        console.log('Connecting to enhanced WebSocket:', wsUrl);
        
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          console.log('Enhanced WebSocket connected');
          setIsConnected(true);
          
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
          }
        };

        ws.onmessage = handleWebSocketMessage;

        ws.onerror = (error) => {
          console.error('WebSocket error:', error);
        };

        ws.onclose = (event) => {
          console.log('WebSocket disconnected, code:', event.code, 'reason:', event.reason);
          setIsConnected(false);
          wsRef.current = null;
          
          // Check if this is a 401 unauthorized close code
          if (event.code === 1008 && event.reason?.includes('401')) {
            const currentPath = window.location.pathname + window.location.search;
            authErrorHandler.handle401Error(currentPath);
            return;
          }
          
          // Only reconnect if we're not intentionally closing and it's not a normal closure
          if (!isLoading && !isNoGuard && event.code !== 1000) {
            reconnectTimeoutRef.current = setTimeout(() => {
              console.log('Attempting to reconnect...');
              connectRef.current?.();
            }, 3000);
          }
        };

      } catch (error) {
        console.error('Failed to connect to WebSocket:', error);
        setIsConnected(false);
        
        // Check if this is a 401 error from getWebSocketToken
        if ((error as any)?.response?.status === 401 || (error as Error)?.message?.includes('401')) {
          const currentPath = window.location.pathname + window.location.search;
          authErrorHandler.handle401Error(currentPath);
          return;
        }
        
        if (!isLoading && !isNoGuard) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connectRef.current?.();
          }, 5000);
        }
      }
    };
  }, [currentModule?.id, isLoading, isNoGuard, handleWebSocketMessage]);

  // Stable connect function
  const connect = useCallback(async () => {
    await connectRef.current?.();
  }, []);

  // Connect on mount
  useEffect(() => {
    if (!isLoading && !isNoGuard && currentModule?.id) {
      connect();
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isNoGuard, currentModule?.id]);

  // Send message function
  const sendMessage = useCallback((text: string) => {
    // Track message count for engagement
    if (engagementTrackerRef.current) {
      engagementTrackerRef.current.incrementMessageCount();
    }
    
    if (isNoGuard) {
      const userMessage: ChatMessage = {
        id: Date.now(),
        text,
        fromUser: true,
        timestamp: new Date().toISOString(),
        userModuleId: currentModule?.id
      };
      setMessages(prev => [...prev, userMessage]);
      
      setCurrentResponse('');
      currentResponseRef.current = '';
      
      setTimeout(() => {
        const aiResponse = getMockAIResponse(text);
        let index = 0;
        
        const typeInterval = setInterval(() => {
          if (index < aiResponse.length) {
            const chunk = aiResponse.substring(index, Math.min(index + 5, aiResponse.length));
            currentResponseRef.current += chunk;
            setCurrentResponse(currentResponseRef.current);
            index += 5;
          } else {
            clearInterval(typeInterval);
            const aiMessage: ChatMessage = {
              id: Date.now() + 1,
              text: aiResponse,
              fromUser: false,
              timestamp: new Date().toISOString(),
              userModuleId: currentModule?.id
            };
            setMessages(prev => [...prev, aiMessage]);
            setCurrentResponse('');
            currentResponseRef.current = '';
          }
        }, 30);
      }, 500);
      
      return;
    }
    
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.warn('Not connected: Please wait for the connection to be established');
      return;
    }

    const userMessage: ChatMessage = {
      text,
      fromUser: true,
      timestamp: new Date().toISOString(),
      userModuleId: currentModule?.id
    };
    
    setMessages(prev => [...prev, userMessage]);
    
    try {
      wsRef.current.send(JSON.stringify({ text }));
    } catch (error) {
      console.error('Failed to send message:', error);
      console.error('Send failed: Failed to send your message.');
    }
  }, [currentModule?.id, isNoGuard]);

  // Request summary generation
  const requestSummary = useCallback(() => {
    if (isNoGuard) {
      // In development mode, simulate summary generation
      setCompletionStage('summarizing');
      setTimeout(() => {
        // Simulate summary draft
        const mockSummary = {
          summaryId: 123,
          templateType: 'reflection',
          editable: true,
          content: {
            template_type: 'reflection',
            sections: {
              keyInsights: "During our conversation, you identified three main areas for leadership development in your emergency department role.",
              personalObservations: "You noticed that your current leadership style tends toward micromanagement when stress levels are high.",
              areasOfGrowth: "The key growth opportunities include delegation, team communication, and work-life balance strategies.",
              challenges: "The main challenges you're facing are physician burnout in your team and managing competing priorities.",
              nextSteps: "Your immediate action items are implementing weekly team check-ins and developing a delegation framework."
            },
            metadata: {
              generated_at: new Date().toISOString(),
              conversation_length: messages.filter(m => !m.fromUser).length,
              module_name: 'AI Coaching'
            }
          }
        };
        
        if (onSummaryDraft) {
          onSummaryDraft(mockSummary);
        }
      }, 2000);
      return;
    }

    // Check WebSocket connection before sending
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected. Cannot request summary.');
      console.error('Summary generation failed: Connection lost. Please refresh and try again.');
      return;
    }

    // Send summary generation request to backend
    console.log('Requesting summary generation from backend...');
    setCompletionStage('summarizing');
    completionStageRef.current = 'summarizing';
    setIsGeneratingSummary(true);
    hasSummaryRef.current = false;
    
    try {
      wsRef.current.send(JSON.stringify({ type: 'generate_summary' }));
      console.log('Sent generate_summary request');
    } catch (error) {
      console.error('Failed to send summary request:', error);
      setCompletionStage('chatting');
      completionStageRef.current = 'chatting';
      setIsGeneratingSummary(false);
      return;
    }
    
    // If no summary arrives after 10 seconds, show an error
    setTimeout(() => {
      if (completionStageRef.current === 'summarizing' && !hasSummaryRef.current) {
        console.error('No summary received after 10 seconds');
        setCompletionStage('chatting');
        completionStageRef.current = 'chatting';
        setIsGeneratingSummary(false);
        console.error('Summary generation failed: The conversation may not have enough engagement (min 0.5 required) or the backend is not responding.');
      }
    }, 10000);
  }, [isNoGuard, messages, onSummaryDraft]);

  // Dismiss completion signal
  const dismissCompletion = useCallback(() => {
    if (isNoGuard) {
      // In development mode, just log
      console.log('Dismissed completion signal (dev mode)');
      return;
    }

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(JSON.stringify({ text: '/dismiss_completion' }));
      } catch (error) {
        console.error('Failed to dismiss completion:', error);
        console.error('Connection error: Unable to dismiss completion signal.');
      }
    } else {
      console.log('WebSocket not available for dismissing completion');
    }
  }, [isNoGuard]);

  // Complete without summary
  const completeWithoutSummary = useCallback(() => {
    if (isNoGuard) {
      // In development mode, just log
      console.log('Completing without summary (dev mode)');
      setCompletionStage('completed');
      return;
    }

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(JSON.stringify({ text: '/complete_without_summary' }));
        setCompletionStage('completed');
        completionStageRef.current = 'completed';
      } catch (error) {
        console.error('Failed to complete without summary:', error);
        console.error('Connection error: Unable to complete module.');
      }
    } else {
      console.error('Not connected: WebSocket connection is not available.');
    }
  }, [isNoGuard]);

  return {
    messages,
    sendMessage,
    isConnected,
    isLoading,
    currentResponse,
    hasActiveResponse: currentResponse.length > 0,
    sessionId,
    completionStage,
    isThinking,
    isGeneratingSummary,
    requestSummary,
    dismissCompletion,
    completeWithoutSummary,
  };
}